/**
 * ImportDetailPage — Issue #176.
 *
 * Per-batch view: summary + row preview with error filters + lifecycle
 * controls (resolve row, approve, commit, cancel) + audit timeline.
 *
 * 红线：
 *   - 所有 mutation 都带 expectedVersion；返回值刷新 batch + 列表。
 *   - 撤销原因必填；errorsJson 的 code/message 由后端权威定义。
 *   - 不展示 cipher / hash / rawPayloadMasked，只展示 last4 + 错误 code/message。
 */
import { useCallback, useEffect, useState } from 'react';
import { Link, useRoute } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { hasPermission, HR_PERMISSIONS } from '@/lib/hrPermissions';
import {
  HR_IMPORT_STATUS_LABELS,
  HR_ROW_ACTION_LABELS,
  HR_ROW_RESOLUTION_LABELS,
  HR_ROW_VALIDATION_LABELS,
  useHrImports,
  type HrEmployeeImportDto,
  type HrEmployeeImportEventDto,
  type HrEmployeeImportRowDto,
  type HrEmployeeImportStatus,
} from '@/hooks/useHr';

const STATUS_TONE: Record<HrEmployeeImportStatus, string> = {
  parsing: 'bg-slate-100 text-slate-700',
  pending_review: 'bg-amber-100 text-amber-800',
  approved: 'bg-blue-100 text-blue-700',
  committed: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  canceled: 'bg-slate-200 text-slate-600',
};
const BADGE_BASE = 'inline-flex rounded-full px-2 py-0.5 text-xs font-medium';

function fmt(value?: string | null): string {
  return value ? new Date(value).toLocaleString('zh-CN') : '—';
}

export default function ImportDetailPage() {
  const [, params] = useRoute<{ id: string }>('/hr/imports/:id');
  const id = params?.id ?? '';
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, HR_PERMISSIONS.IMPORT_READ);
  const canWrite = hasPermission(permissions, HR_PERMISSIONS.IMPORT_WRITE);

  const {
    error,
    fetchImport,
    fetchImportRows,
    fetchImportEvents,
    resolveImportRow,
    approveImport,
    commitImport,
    cancelImport,
  } = useHrImports();

  const [batch, setBatch] = useState<HrEmployeeImportDto | null>(null);
  const [rows, setRows] = useState<HrEmployeeImportRowDto[]>([]);
  const [events, setEvents] = useState<HrEmployeeImportEventDto[]>([]);
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    if (!id) return;
    const next = await fetchImport(id);
    if (next) setBatch(next);
    const rowResp = await fetchImportRows(id, { errorsOnly, limit: 100 });
    if (rowResp) setRows([...rowResp.items]);
    const eventResp = await fetchImportEvents(id);
    if (eventResp) setEvents(eventResp);
  }, [id, fetchImport, fetchImportRows, fetchImportEvents, errorsOnly]);

  useEffect(() => {
    if (!canRead) return;
    void refresh();
  }, [canRead, refresh]);

  if (!canRead) return <AccessDenied resource="导入批次详情" />;
  if (!batch) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        加载中… {error ? <span className="text-red-700">{error.message}</span> : null}
      </div>
    );
  }

  const current = batch;
  const showApprove =
    canWrite && current.status === 'pending_review' && current.errorRows + current.duplicateRows === 0;
  const showCommit = canWrite && current.status === 'approved';
  const showCancel =
    canWrite && (current.status === 'pending_review' || current.status === 'approved');
  const showResolve = canWrite && current.status === 'pending_review';

  async function handleSkipRow(row: HrEmployeeImportRowDto) {
    if (!showResolve) return;
    setBusy(true);
    const result = await resolveImportRow(id, row.id, {
      expectedVersion: current.version,
      resolutionStatus: 'skipped',
      rowAction: 'skip',
    });
    setBusy(false);
    if (result) await refresh();
  }

  async function handleApprove() {
    if (!showApprove) return;
    setBusy(true);
    const result = await approveImport(id, { expectedVersion: current.version });
    setBusy(false);
    if (result) setBatch(result);
    await refresh();
  }

  async function handleCommit() {
    if (!showCommit) return;
    setBusy(true);
    const result = await commitImport(id, { expectedVersion: current.version });
    setBusy(false);
    if (result) setBatch(result);
    await refresh();
  }

  async function handleCancel() {
    if (!showCancel) return;
    const trimmed = cancelReason.trim();
    if (trimmed.length === 0) return;
    setBusy(true);
    const result = await cancelImport(id, {
      expectedVersion: current.version,
      reason: trimmed,
    });
    setBusy(false);
    if (result) setBatch(result);
    setCancelReason('');
    await refresh();
  }

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">
            员工导入批次{' '}
            <span className="ml-2 font-mono text-sm text-muted-foreground">
              {current.id.slice(0, 12)}…
            </span>
          </h1>
          <p className="text-sm text-slate-500">{current.sourceFilename}</p>
        </div>
        <Link href="/hr/imports" className="text-sm text-primary hover:underline">
          ← 返回列表
        </Link>
      </header>

      <Card className="grid grid-cols-2 gap-4 p-4 text-sm md:grid-cols-4">
        <div>
          <div className="text-xs text-muted-foreground">状态</div>
          <span className={cn(BADGE_BASE, STATUS_TONE[current.status])}>
            {HR_IMPORT_STATUS_LABELS[current.status]}
          </span>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">总行 / 合法 / 错误 / 重复</div>
          <div className="font-mono text-sm">
            {current.totalRows} / {current.validRows} / {current.errorRows} / {current.duplicateRows}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">已新增 / 已更新 / 已跳过</div>
          <div className="font-mono text-sm">
            {current.insertedCount} / {current.updatedCount} / {current.skippedCount}
          </div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">版本号</div>
          <div className="font-mono text-sm">v{current.version}</div>
        </div>
        <div className="col-span-2">
          <div className="text-xs text-muted-foreground">文件 SHA-256</div>
          <div className="break-all font-mono text-xs">{current.sourceSha256}</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">创建</div>
          <div className="text-xs">{fmt(current.createdAt)}（{current.createdBy.slice(0, 8)}）</div>
        </div>
        <div>
          <div className="text-xs text-muted-foreground">提交</div>
          <div className="text-xs">{fmt(current.committedAt)}</div>
        </div>
      </Card>

      {error ? (
        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error.message}
        </div>
      ) : null}

      <Card className="space-y-3 p-4">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">行级预览</h2>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={errorsOnly}
              onChange={(e) => setErrorsOnly(e.target.checked)}
            />
            只看错误 / 重复行
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b bg-muted/40">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">行号</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">工号</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">姓名</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">部门</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">身份证</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">手机</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">校验</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">处置</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">动作</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">错误</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">操作</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b last:border-0 hover:bg-muted/20">
                  <td className="px-3 py-2 font-mono text-xs">{row.rowNumber}</td>
                  <td className="px-3 py-2 font-mono text-xs">{row.employeeNo ?? '—'}</td>
                  <td className="px-3 py-2 text-xs">{row.name ?? '—'}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{row.department ?? '—'}</td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {row.idCardLast4 ? `***${row.idCardLast4}` : '—'}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">
                    {row.phoneLast4 ? `***${row.phoneLast4}` : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {HR_ROW_VALIDATION_LABELS[row.validationStatus]}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {HR_ROW_RESOLUTION_LABELS[row.resolutionStatus]}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {HR_ROW_ACTION_LABELS[row.rowAction]}
                  </td>
                  <td className="px-3 py-2 text-xs text-red-700">
                    {row.errors.length === 0
                      ? '—'
                      : row.errors
                          .map((e) => `${e.field ? `[${e.field}] ` : ''}${e.message}`)
                          .join('；')}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {showResolve && row.resolutionStatus === 'pending' &&
                    (row.validationStatus === 'error' ||
                      row.validationStatus === 'duplicate') ? (
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => void handleSkipRow(row)}
                        className="rounded border px-2 py-1 text-xs hover:bg-muted disabled:opacity-50"
                      >
                        标记跳过
                      </button>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="space-y-3 p-4">
        <h2 className="text-sm font-semibold">生命周期操作</h2>
        <div className="flex flex-wrap items-end gap-3">
          {showApprove ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleApprove()}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
            >
              审核通过
            </button>
          ) : null}
          {showCommit ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void handleCommit()}
              className="rounded-md bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700 disabled:opacity-50"
            >
              正式导入
            </button>
          ) : null}
          {showCancel ? (
            <div className="flex items-end gap-2">
              <label className="text-xs">
                撤销原因（必填，审计留痕）
                <input
                  type="text"
                  className="mt-1 block w-72 rounded border border-slate-300 px-2 py-1 text-sm"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  maxLength={2000}
                />
              </label>
              <button
                type="button"
                disabled={busy || cancelReason.trim().length === 0}
                onClick={() => void handleCancel()}
                className="rounded-md border border-red-300 px-4 py-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                撤销批次
              </button>
            </div>
          ) : null}
          {!showApprove && !showCommit && !showCancel ? (
            <span className="text-xs text-muted-foreground">
              批次已进入终态或没有可执行的操作。
            </span>
          ) : null}
        </div>
      </Card>

      <Card className="space-y-2 p-4">
        <h2 className="text-sm font-semibold">审计时间线</h2>
        <ol className="space-y-2 text-xs">
          {events.map((evt) => (
            <li key={evt.id} className="border-b pb-2 last:border-0">
              <div className="font-mono text-muted-foreground">{fmt(evt.createdAt)}</div>
              <div>
                <strong>{evt.action}</strong>{' '}
                <span className="text-muted-foreground">
                  ({evt.fromState ?? '—'} → {evt.toState})
                </span>{' '}
                by {evt.actorId.slice(0, 8)}
              </div>
              {evt.note ? <div className="text-muted-foreground">{evt.note}</div> : null}
            </li>
          ))}
          {events.length === 0 ? (
            <li className="text-muted-foreground">暂无事件</li>
          ) : null}
        </ol>
      </Card>
    </div>
  );
}
